/**
 * Banner shown on the Bank Accounts page listing any Teller connections
 * marked as `status: 'disconnected'` (typically because MFA expired or the
 * user revoked access at their bank). One click opens the Teller SDK with
 * the stored `enrollmentId`, Teller walks the user through re-auth, and we
 * patch the existing connection with the fresh access token — no new
 * BankAccount is created so historical transactions stay linked.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertCircle, Loader2, RefreshCw } from "lucide-react";

export default function TellerReconnectBanner({ onReconnected }) {
  const [config, setConfig] = useState(null);
  const [disconnected, setDisconnected] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [cfg, { data }] = await Promise.all([
        api.get("/bank-connect/config").then((r) => r.data).catch(() => null),
        api.get("/bank-connections"),
      ]);
      setConfig(cfg);
      // Backend truncates `access_token` for list responses, so we also
      // need to re-fetch the full connection record right before Reconnect
      // (not strictly needed because reconnect sends a NEW access token).
      setDisconnected((data || []).filter((c) => c.status === "disconnected"));
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!config?.configured) return null;
  if (!disconnected.length) return null;

  const reconnect = async (conn) => {
    if (typeof window.TellerConnect === "undefined") {
      toast.error("Teller Connect failed to load. Refresh the page and try again.");
      return;
    }
    setBusyId(conn.id);
    const teller = window.TellerConnect.setup({
      applicationId: config.application_id,
      environment: config.environment || "sandbox",
      enrollmentId: conn.enrollment_id,  // key for re-auth flow
      products: ["transactions", "balance"],
      onSuccess: async (enrollment) => {
        try {
          const accessToken = enrollment?.accessToken || enrollment?.access_token;
          if (!accessToken) { toast.error("Teller returned no access token"); return; }
          await api.post(`/bank-connections/${conn.id}/reconnect`, {
            access_token: accessToken,
            enrollment_id: enrollment?.enrollment?.id || conn.enrollment_id,
            provider_account_id: conn.provider_account_id,
            institution_name: conn.institution_name,
          });
          toast.success(`${conn.institution_name || "Bank"} reconnected`);
          // Trigger an immediate sync so the user sees fresh data.
          try { await api.post(`/bank-connections/${conn.id}/sync`); } catch {}
          await load();
          onReconnected?.();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Reconnect failed");
        } finally { setBusyId(null); }
      },
      onExit: () => setBusyId(null),
      onFailure: (f) => { toast.error(f?.message || "Teller reconnect failed"); setBusyId(null); },
    });
    teller.open();
  };

  return (
    <div
      className="border-l-4 p-4 mb-6 bg-[#FEF2F2]"
      style={{ borderColor: "#DC2626" }}
      data-testid="teller-reconnect-banner"
    >
      <div className="flex items-start gap-3">
        <AlertCircle size={18} className="text-[#DC2626] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base text-[#7F1D1D] mb-1">
            {disconnected.length === 1 ? "1 bank needs to reconnect" : `${disconnected.length} banks need to reconnect`}
          </div>
          <p className="text-sm text-[#7F1D1D] mb-3 leading-relaxed">
            Your bank revoked access or an MFA step expired. Reconnect to resume automatic syncs — your historical transactions stay in place.
          </p>
          <div className="space-y-2" data-testid="teller-reconnect-list">
            {disconnected.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between bg-white border px-3 py-2"
                style={{ borderColor: "#FCA5A5" }}
                data-testid={`teller-reconnect-row-${c.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{c.institution_name || "Linked bank"}</div>
                  {c.error_message && <div className="text-xs text-[#B91C1C]">{c.error_message}</div>}
                </div>
                <button
                  type="button"
                  onClick={() => reconnect(c)}
                  disabled={busyId === c.id}
                  data-testid={`teller-reconnect-btn-${c.id}`}
                  className="text-xs px-3 py-1.5 bg-[#DC2626] text-white hover:bg-[#B91C1C] flex items-center gap-1.5 disabled:opacity-60"
                >
                  {busyId === c.id ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                  {busyId === c.id ? "Opening Teller…" : "Reconnect"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
